import React, { useMemo, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Navbar.css';
import { useAuth } from '../contexts/AuthContext';
import { ACCOUNT_PREVIEW } from '../utils/accountPreview';
import { useLanguage } from '../contexts/LanguageContext';
import { Language, SUPPORTED_LANGUAGES } from '../i18n/languages';
import { getPreviewAvatarUrl, profileAvatarUrl } from '../services/userApi';

interface NavbarProps {
  cartCount: number;
  onCartClick: () => void;
}

function getInitials(name: string, email: string): string {
  const src = (name || email).trim();
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
  }
  return src.slice(0, 2).toUpperCase();
}

const Navbar: React.FC<NavbarProps> = ({ cartCount, onCartClick }) => {
  const location = useLocation();
  const { language, setLanguage, t } = useLanguage();
  const { isAuthenticated, user } = useAuth();
  const accountPath = ACCOUNT_PREVIEW || isAuthenticated ? '/cuenta' : '/cuenta/entrar';
  const accountActive = location.pathname.startsWith('/cuenta');
  const signedIn = (ACCOUNT_PREVIEW || isAuthenticated) && !!user;

  const avatarSrc = useMemo(() => {
    if (!signedIn || !user) return null;
    if (ACCOUNT_PREVIEW) {
      const preview = getPreviewAvatarUrl();
      if (preview) return preview;
    }
    if (user.has_avatar) return profileAvatarUrl();
    return null;
  }, [signedIn, user]);

  const initials = useMemo(() => {
    if (!user) return '';
    return getInitials(user.display_name || '', user.email || '');
  }, [user]);
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const getActiveSection = () => {
    const path = location.pathname;
    if (path === '/') return 'home';
    if (path === '/schools') return 'schools';
    if (path === '/how-it-works') return 'how-it-works';
    if (path === '/about-me') return 'sobre-mi';
    if (path === '/pricing') return 'pricing';
    if (path === '/faqs') return 'faqs';
    return '';
  };

  const activeSection = getActiveSection();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.language-selector')) {
        setShowLanguageMenu(false);
      }
    };

    if (showLanguageMenu) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showLanguageMenu]);

  useEffect(() => {
    setMobileMenuOpen(false);
    setShowLanguageMenu(false);
  }, [location.pathname]);

  const handleLanguageChange = (lang: Language) => {
    setLanguage(lang);
    setShowLanguageMenu(false);
  };

  const handleNavLinkClick = () => {
    setMobileMenuOpen(false);
  };

  const handleCartClick = () => {
    setMobileMenuOpen(false);
    onCartClick();
  };

  const navItems = [
    { id: 'schools', label: t.navbar.schools, path: '/schools' },
    { id: 'how-it-works', label: t.navbar.howItWorks, path: '/how-it-works' },
    { id: 'sobre-mi', label: t.navbar.aboutMe, path: '/about-me' },
    { id: 'pricing', label: t.navbar.pricing, path: '/pricing' },
    { id: 'faqs', label: t.navbar.faqs, path: '/faqs' },
  ];

  const currentLanguage =
    SUPPORTED_LANGUAGES.find((l) => l.code === language) ?? SUPPORTED_LANGUAGES[1];

  const languageSelector = (
    <div className="language-selector">
      <button
        type="button"
        className={`language-button ${showLanguageMenu ? 'is-open' : ''}`}
        onClick={() => setShowLanguageMenu(!showLanguageMenu)}
        aria-expanded={showLanguageMenu}
        aria-haspopup="listbox"
        aria-label="Idioma"
      >
        <img
          src={currentLanguage.flag}
          alt=""
          className="language-flag"
          width={20}
          height={15}
        />
        <svg
          className={`language-arrow ${showLanguageMenu ? 'open' : ''}`}
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          aria-hidden="true"
        >
          <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </button>

      {showLanguageMenu && (
        <div className="language-menu" role="listbox">
          {SUPPORTED_LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              type="button"
              role="option"
              aria-selected={language === lang.code}
              className={`language-option ${language === lang.code ? 'active' : ''}`}
              onClick={() => handleLanguageChange(lang.code)}
            >
              <img
                src={lang.flag}
                alt=""
                className="language-flag"
                width={20}
                height={15}
              />
              <span className="language-label">{lang.label}</span>
              {language === lang.code && (
                <svg className="check-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path
                    d="M3 8L6 11L13 4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const accountIconButton = (onNavigate?: () => void) => (
    <Link
      to={accountPath}
      className={`account-button${signedIn ? ' account-button--avatar' : ''} ${accountActive ? 'active' : ''}`}
      aria-label={signedIn && user?.display_name ? user.display_name : t.navbar.myAccount}
      title={signedIn && user?.display_name ? user.display_name : t.navbar.myAccount}
      onClick={onNavigate}
    >
      {signedIn ? (
        avatarSrc ? (
          <img
            key={`${user?.id}-${user?.has_avatar}`}
            src={avatarSrc}
            alt=""
            className="account-avatar-img"
          />
        ) : (
          <span className="account-avatar-initials" aria-hidden="true">
            {initials}
          </span>
        )
      ) : (
        <svg
          className="account-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      )}
    </Link>
  );

  const cartButton = (
    <button type="button" className="cart-button" onClick={handleCartClick}>
      <svg className="cart-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <circle cx="9" cy="21" r="1" />
        <circle cx="20" cy="21" r="1" />
        <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
      </svg>
      <span className="cart-button-text">{t.navbar.memories}</span>
      {cartCount > 0 && <span className="cart-count">{cartCount}</span>}
    </button>
  );

  return (
    <nav className="navbar">
      <div className="navbar-wrapper">
        <Link to="/" className="navbar-brand">
          <img src="/image.png" alt="SurfShot" className="brand-logo" />
        </Link>

        <button
          type="button"
          className={`mobile-menu-button ${mobileMenuOpen ? 'mobile-menu-button-open' : ''}`}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label={mobileMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
          aria-expanded={mobileMenuOpen}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            {mobileMenuOpen ? (
              <>
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </>
            ) : (
              <>
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </>
            )}
          </svg>
        </button>

        <ul className={`navbar-menu ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          {mobileMenuOpen && (
            <div className="navbar-actions mobile-actions-top">
              {accountIconButton(handleNavLinkClick)}
              {languageSelector}
              {cartButton}
            </div>
          )}

          {navItems.map((item) => (
            <li key={item.id}>
              <Link
                to={item.path}
                className={`nav-link ${activeSection === item.id ? 'active' : ''}`}
                onClick={handleNavLinkClick}
              >
                {item.label}
              </Link>
            </li>
          ))}
        </ul>

        <div className="navbar-actions desktop-actions">
          {accountIconButton()}
          {languageSelector}
          {cartButton}
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="mobile-menu-overlay" onClick={() => setMobileMenuOpen(false)} aria-hidden="true" />
      )}
    </nav>
  );
};

export default Navbar;
